import { DateTime } from 'luxon';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';

import ProgressBar from 'components/progressBar';
import ResistCTAButtons from 'components/buttons/cta';
import Anchor from 'components/typography/anchor';
import Bubble from 'components/bubble';
import Layout, { DATA_TYPES } from 'components/layout';
import { ShareBlock } from 'components/petition/shareblock';
import { PETITION_TIERS } from 'components/petition/config';
import Heading from 'components/typography/heading';
import Text from 'components/typography/text';
import petitionShareImg from 'images/shares/petition-share.png';
import getTargetTypeHumanName from 'utilities/targets';
import { PetitionType } from 'utilities/propTypes';
import { getTrendingPetitions } from 'lib/api/petitions';

import {
  trimPetitions,
  fieldsFullPetition,
  PETITION_SIGNING_STATES,
} from 'lib/petitions';

import {
  petitionContainer,
  petitionBody,
  petitionSidebar,
  petitionMasthead,
  progressBar,
  dateCreated,
  letterTarget,
  message,
  moreSigs,
} from './petition-detail.module.scss';
import { cta, ctaHeading } from '../common/petitions.module.scss';

const dateFormatWithoutYear = 'LLLL d'; // ie. October 20
const now = DateTime.local();
const PETITIONS_LOOKUP_URL = `https://cdn.prod.resistbot.cloud/petitions/dynamic/lookup`;
const REQUEST_OPTIONS = {
  headers: {
    'User-Agent': 'Ginrickey',
  },
};

export const getServerSideProps = async ({ params }) => {
  const sharecode = params.sharecode.toUpperCase();
  // Note: This logic hasn't been moved to /api/petitions yet because it's not clear the best way
  // to do error handling etc.  Could be moved there if that issue is clarified.
  const url = new URL(PETITIONS_LOOKUP_URL);
  url.search = new URLSearchParams({
    message_code: sharecode,
    expand: 'user',
  }).toString();

  // These take approximately the same time, and a 404 is probably way less common than finding a matched
  // petition, so we do both, knowing that in most cases, we save half the time, 404's take ~2x longer.
  const [petitionResponse, trendingPetitions] = await Promise.all([
    fetch(url, REQUEST_OPTIONS),
    getTrendingPetitions(),
  ]);

  if (400 <= petitionResponse.status && petitionResponse.status < 500) {
    return {
      notFound: true,
    };
  }

  // Fetch petition data
  const petition = await petitionResponse.json();
  const isTrending = trendingPetitions.filter(
    (p) => p.petition_code === sharecode
  );

  // Move this to secondary level for if we ever use different time periods
  // This also happens in packages/site/src/lib/api/petitions/index.js
  petition['trending'] = {
    sign_count_period: isTrending[0]?.trending?.sign_count_period || null,
  };

  const slimPetition = trimPetitions({
    petitions: [petition],
    fieldsToKeep: fieldsFullPetition,
  })[0];

  return {
    props: {
      petition: slimPetition,
    },
  };
};

const PetitionMetadata = ({ dateString, petition, userCode }) => {
  const name = userCode ? (
    <Anchor href={`/go/${userCode}`}>{petition.petitioner_name}</Anchor>
  ) : (
    petition.petitioner_name
  );

  return (
    <Text as="p" className={dateCreated}>
      First sent <em>on</em> {dateString} <em>by</em> {name}{' '}
      {petition.trending.sign_count_period
        ? `· ${petition.trending.sign_count_period.toLocaleString()} signers in
            the past 7 days`
        : null}
    </Text>
  );
};

const Petition = ({ petition }) => {
  const router = useRouter();
  const url = router.asPath;

  const hasState = !!petition.state;
  const stateDetail = `(${petition.state} only) `;
  const targetDetail = getTargetTypeHumanName(petition.target);

  const date = DateTime.fromSeconds(petition.sent_timestamp);
  const isThisYear = date.year === now.year;
  const dateString = isThisYear
    ? date.toFormat(dateFormatWithoutYear)
    : date.toLocaleString(DateTime.DATE_FULL);
  const subject = petition.subject
    ? petition.subject
    : petition.message.substring(0, 79);
  const is_closed = petition.sign_status === PETITION_SIGNING_STATES.CLOSED;

  return (
    <Layout
      title={`Resistbot Petition: ${subject}`}
      ogTitle={subject}
      dataType={DATA_TYPES.petition}
      description={`Text SIGN ${petition.petition_code} to 50409 to send this to your officials.`}
      image={petitionShareImg}
      imageAltText='A photo of a protest with signs like "immigrants make America great," "support equal rights," and, "we will not be quiet we will not back down" overlaid with white text reading "take action."'
      canonicalPath={`/petitions/${petition.petition_code}`}
    >
      <div className={petitionMasthead}>
        <Text as="span" className={letterTarget}>
          An open letter <em>to</em> {targetDetail}{' '}
          {hasState ? stateDetail : ''}
        </Text>
        <Heading as="h1" size="h1">
          {subject}
        </Heading>
        {!is_closed ? (
          <>
            <div className={progressBar}>
              <ProgressBar
                current={petition.sign_count}
                tiers={PETITION_TIERS}
                label={'signers'}
              />
            </div>
          </>
        ) : null}
      </div>

      <div className={petitionContainer}>
        <div className={petitionBody}>
          <Text className={message}>{petition.message}</Text>

          <PetitionMetadata
            dateString={dateString}
            petition={petition}
            userCode={petition.petitioner_code}
          />
        </div>

        <div className={petitionSidebar}>
          <CTA sharecode={petition.petition_code} is_closed={is_closed} />

          <div className={moreSigs}>
            <Heading as="h3" className={ctaHeading} size="h3">
              Already signed?
            </Heading>

            <ol>
              <li>
                Text <Bubble>PROMOTE {petition.petition_code}</Bubble> to drive
                more signers.
              </li>

              <li>
                Print this and post around campus or on your community bulletin
                board!
              </li>

              <li>
                Use the{' '}
                <Anchor href="https://apps.apple.com/us/app/resistbot/id1564775862">
                  iOS app
                </Anchor>{' '}
                to share with your contacts!
              </li>
            </ol>

            <ShareBlock
              pathname={url}
              text={`Text SIGN ${petition.petition_code} to 50409`}
            />
          </div>
        </div>
      </div>
    </Layout>
  );
};

const CTA = ({ sharecode, is_closed = false }) => {
  const params = !is_closed ? { keyword: `Sign ${sharecode}` } : {};
  const heading = !is_closed ? (
    <Heading as="h2" className={ctaHeading} size="h2">
      Send to Your Officials
    </Heading>
  ) : null;

  return (
    <div className={cta}>
      {heading}
      <ResistCTAButtons {...params} />
    </div>
  );
};

CTA.propTypes = {
  sharecode: PropTypes.string.isRequired,
  is_closed: PropTypes.bool,
};

Petition.propTypes = {
  petition: PetitionType,
};

export default Petition;
